import { Alert } from '@televet/kibble-ui';
import { Box } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useModularity } from 'shared/hooks/useModularity';
import { UAParser } from 'ua-parser-js';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import useClinicUser from 'shared/hooks/useClinicUser';

const APPLE_APP_STORE_URL = 'https://apps.apple.com/us/app/ai-recap/id6748966993';
const GOOGLE_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.televet.recapmobile';

const RecapDownloadBanner = (): JSX.Element | null => {
  const { isRecapClinic } = useModularity();
  const { clinicUserId } = useClinicUser();
  const userKey = `User:${clinicUserId}`;
  const [isBannerHidden, setIsBannerHidden] = usePersistedState<
    Record<string, boolean>
  >(PersistedStateKeys.AiRecapHideAppBanner, {});

  const handleDownloadClick = useCallback((): void => {
    const parser = new UAParser();
    const isApple = ['ios', 'mac os', 'macos'].includes(parser.getOS().name?.toLowerCase() ?? '');
    if (isApple) {
      window.open(APPLE_APP_STORE_URL, '_blank');
    } else {
      window.open(GOOGLE_PLAY_STORE_URL, '_blank');
    }
  }, []);

  const updateIsBannerHidden = useCallback(
    (value: boolean): void => {
      if (userKey) {
        setIsBannerHidden((prevState) => ({
          ...(!!prevState ? prevState : {}),
          [userKey]: value,
        }));
      }
    },
    [userKey, setIsBannerHidden],
  );

  if (!isRecapClinic || (isBannerHidden && userKey && isBannerHidden[userKey])) {
    return null;
  }

  return (
    <Box p="12px" w="100%" className="RecapDownloadBanner">
      <Alert
        w="100%"
        bg="background.info"
        hideIcon
        title="Download the AI Recap mobile app - the most convenient way to record appointments on-the-go."
        buttonProps={{
          buttonText: 'Download App',
          buttonPosition: 'bottomLeft',
        }}
        onClick={handleDownloadClick}
        onClose={(ev): void => {
          ev?.stopPropagation();
          updateIsBannerHidden(true);
        }}
      />
    </Box>
  );
};

export default RecapDownloadBanner;
